// Role-visibility helpers — single source of truth for which roles
// can see which surfaces in the UI.
//
// These predicates are consumed by:
//   * TopNav (Automations item)
//   * Sidebar (Admin section)
//   * RequireRole route guards in App.tsx
//   * Future: any feature flag tied to role
//
// They take a structural subset of SessionUser (or null) so we can
// wire them straight off useSession().user without per-call adapters.
// Inlined locally — NOT imported from `../api` — because this file is
// reachable from tests/web/ which gets type-checked under the root
// tsconfig, and api.ts uses Vite + DOM globals that the root config
// doesn't carry types for.
//
// Server-side enforcement is the real security boundary; these
// client-side checks are UX (don't show a button the user can't use).

import { isFeeReviewPerson } from "@shared/fee-review/access";
import { isPmEditor } from "@shared/private-money/pm-editors";

export interface RoleAwareUserShape {
  // User id is included here so per-user dashboard/sidebar overrides
  // (Brandon's book-of-business shortcuts, Joy's pipeline overlay,
  // etc.) can dispatch without a second-level session fetch. Server
  // remains the real security boundary — this is UX identity only.
  id?: string;
  employee_role_slug: string | null;
  // Display name, when available — the sidebar's own section is headed
  // with the user's first name and nothing else (Grant, 2026-08-23:
  // "Name (role not needed)"). Optional so pure role-gate callers (and
  // unit tests) can pass a minimal user. UX identity only, not security.
  name?: string | null;
  // Company scope (users.company_scope → the session's
  // editable_company_slugs): an ARRAY of slugs, or "all" for the
  // unscoped roles (system_admin / compliance / front_desk).
  //
  // Added 2026-08-13 so nav gates can express "your companies include X"
  // rather than only "your ROLE is X". Some surfaces are owned by a
  // company, not a rank — the IVANS review queue is for whoever services
  // the insurance book, which is a scope question and never a role
  // question. Optional, so every existing caller and test fixture that
  // passes a role-only user keeps compiling and keeps its old answer.
  editable_company_slugs?: string[] | "all" | null;
  // Sign-in email, when available. Added 2026-09-01 for the one gate
  // that is PERSON-scoped rather than role- or company-scoped (the
  // Monthly Fee Review — Grant's call: "just me, Baker, and Jake").
  // Optional so role-only callers and test fixtures keep compiling.
  // UX identity only, not security — the server gate checks the same
  // email independently.
  email?: string | null;
}
export type RoleAwareUser = RoleAwareUserShape | null;

// Admin section in the sidebar + Automations top-nav item.
// Per CLAUDE.md the system_admin and compliance roles get visibility
// into Automations + the Admin nav cluster. compliance gets read-only
// access to most admin surfaces; the actual write capability is
// gated server-side per endpoint.
export function canSeeAdminSection(user: RoleAwareUser): boolean {
  const slug = user?.employee_role_slug ?? null;
  return slug === "system_admin" || slug === "compliance";
}

export function canSeeAutomationsNav(user: RoleAwareUser): boolean {
  return canSeeAdminSection(user);
}

// The commission reconciliation report — "are we getting paid on the
// policies we sold?" — is a SHARED surface (Grant, 2026-08-28: "in an
// ideal world it is something that goes to Brandon and I together").
//
// Deliberately wider than canSeeAdminSection, which is system_admin +
// compliance only and would have made this Grant's report alone. Adding
// company_admin brings in Brandon, who owns the Insurance carrier
// relationships and is the person who would actually chase a carrier.
// Mirrors the server gate on /api/admin/commission-reconciliation/summary.
export function canSeeCommissionReconciliation(user: RoleAwareUser): boolean {
  const slug = user?.employee_role_slug ?? null;
  return slug === "system_admin" || slug === "compliance" || slug === "company_admin";
}

// Company Coverage — Stewardship's OWN insurance (Grant, 2026-09-29: "any
// admin or company owner"). system_admin sees and edits everything;
// company_admin (Brandon, Jake, Jean) reads the business policies that cover
// their companies. Compliance is deliberately not included. Mirrors
// canOpenCompanyCoverage in shared/company-coverage/access.ts, which the
// server enforces on every request; this only keeps the menu honest.
export function canSeeCompanyCoverage(user: RoleAwareUser): boolean {
  const slug = user?.employee_role_slug ?? null;
  return slug === "system_admin" || slug === "company_admin";
}

/** The pages a SHARED policy opens on (Company Coverage step 4): any
 *  employee may reach them from a "shared with you" notice, and the server
 *  answers with only what was shared (or "not found"). The rail item stays
 *  canSeeCompanyCoverage; this only keeps a shared link from dead-ending. */
export function canOpenSharedCompanyPolicy(user: RoleAwareUser): boolean {
  const slug = user?.employee_role_slug ?? null;
  return !!slug && slug !== "ambassador";
}

// Gate 2 (Grant 2026-06-01) — ANY employee may open the Automations
// workspace. Non-admins see + edit only the rules they created (server
// scopes list/detail to owner); admins additionally see all rules + the
// governance controls (kill switch, ownership health). This is the
// route/nav gate; per-row ownership is enforced server-side.
export function canSeeAutomations(user: RoleAwareUser): boolean {
  return !!user?.employee_role_slug;
}

// Global automation kill switch — ARMING it is system_admin only, matching
// the requireSystemAdmin gate on POST /admin/automations/global-pause.
// Deliberately NARROWER than canSeeAdminSection (which also returns true for
// compliance): a compliance user rendering the button would get a 403 at the
// exact moment they're trying to stop everything, and an incident is the
// worst time to discover a dead control. Reading the state stays open to
// every employee — the "everything is paused" banner must show for all.
export function canArmAutomationKillSwitch(user: RoleAwareUser): boolean {
  return user?.employee_role_slug === "system_admin";
}

// v1.x phase 11.16 (Tier A item 5) — /admin/sharepoint-migrate is
// system_admin only because the underlying endpoint moves files in
// SharePoint and rewrites documents rows. Compliance role doesn't
// need this affordance.
export function canRunSharePointMigration(user: RoleAwareUser): boolean {
  return user?.employee_role_slug === "system_admin";
}

// Bulk "advisor departed" fit cleanup — system_admin only (Grant +
// Baker). It writes fit history across many relationships at once, so it
// lives with the other system_admin-only backbone actions.
export function canRunFitCleanup(user: RoleAwareUser): boolean {
  return user?.employee_role_slug === "system_admin";
}

// /admin/team is system_admin only — invite issuance and account
// lifecycle changes can only originate from a true system admin per
// the spec in CLAUDE.md "Authentication" + the user-provisioning
// runbook.
export function canSeeTeamPage(user: RoleAwareUser): boolean {
  return user?.employee_role_slug === "system_admin";
}

export function canSeeAdminTeamInvite(user: RoleAwareUser): boolean {
  return canSeeTeamPage(user);
}

// /admin/shared-mailboxes — system_admin only. Granting an employee
// visibility of a shared mailbox is an access-control change, so it
// lives with the other system_admin-only account surfaces. (The
// backend route is requireSystemAdmin regardless.)
export function canManageSharedMailboxAccess(user: RoleAwareUser): boolean {
  return user?.employee_role_slug === "system_admin";
}

// Pay rules live on each person's comp plan (build step B2); only system
// admins see or edit them, through the Team page (canSeeTeamPage above).

// Split override authority — admin OR the user who owns the entity
// (opportunity/transaction owner_user_id matches). Caller passes the
// entity's owner id in.
export function canSetSplitOverride(user: RoleAwareUser, entityOwnerId: string | null): boolean {
  if (!user) return false;
  if (user.employee_role_slug === "system_admin") return true;
  return user.id === entityOwnerId;
}

// Gross-to-company + brokerage-retained cents are admin-only. Advisors
// NEVER see these values even when they own the entity — per Grant
// 2026-04-24 the intent is advisors see only their own comp, not the
// company's gross on the deal.
export function canSeeGrossCompany(user: RoleAwareUser): boolean {
  return user?.employee_role_slug === "system_admin";
}

// Impersonation visibility.
//
// The "Impersonate" button shows in /admin/team for system admins
// only. As of the v1.1 amendment, the button ALSO shows on
// system_admin target rows: Stewardship's co-founder team (Grant +
// Brian Baker) holds equal admin rights and needs mutual QA + demo
// capability. Admin-on-admin flows are allowed at every layer
// (UI, start endpoint, session manager) and flagged (deeper orange
// banner + `impersonation_admin_on_admin_started` audit event) so
// the sensitivity is unmissable.
//
// Self-impersonation is still blocked — enforced at the page layer
// via a direct `target.id === currentUser.id` check in
// AdminTeamPage, since we don't pass the current user's id into
// this helper.
//
// Soft-deleted / inactive users don't show either — the
// impersonation would fail on start and the button would mislead.
export interface ImpersonateCandidate {
  employee_role_slug: string | null;
  is_active: boolean;
}

export function canImpersonateUser(
  currentUser: RoleAwareUser,
  candidate: ImpersonateCandidate,
): boolean {
  if (!canSeeTeamPage(currentUser)) return false;
  if (!candidate.is_active) return false;
  if (!candidate.employee_role_slug) return false;
  // Admin-on-admin is allowed in v1.1. Self-impersonation is blocked
  // at the AdminTeamPage level via the `isSelf` check.
  return true;
}

// True when a system_admin is about to impersonate another
// system_admin. Drives the deeper-orange banner variant and the
// ADMIN-ON-ADMIN badge on the impersonation log.
export function isAdminOnAdminImpersonation(targetRoleSlug: string | null): boolean {
  return targetRoleSlug === "system_admin";
}

// IVANS match-review queue (/insurance/ivans-review) — "Check carrier
// data". Grant, 2026-08-13: "This matching of info from IVANS needs to be
// available for all Insurance users."
//
// SCOPE, NOT ROLE. The people who can answer "did this carrier
// transaction land on the right family?" are the ones who service the
// insurance book — Mylah (back_office), Joy (advisor), Brandon
// (company_admin). Three different roles, one company. So this gates on
// company scope, which is also what the server enforces
// (mayActOnCompany(actor, "insurance") in routes/ivans-review.ts).
//
// "all" (system_admin / compliance / front_desk — Grant, Baker, Donna)
// passes too. This deliberately differs from isInsuranceLensUser(), which
// excludes "all" because it decides a FIELD LAYOUT preference — a thing
// only daily quoters want. This decides whether a page is reachable, and
// Grant reading the grading queue is the whole point of the exercise.
//
// A user with no scope information at all (a minimal fixture, an
// unloaded session) gets false — fail closed, and the server would
// refuse them anyway.
export function canSeeIvansReview(user: RoleAwareUser): boolean {
  if (!user?.employee_role_slug) return false;
  const scope = user.editable_company_slugs;
  if (scope === "all") return true;
  return Array.isArray(scope) && scope.includes("insurance");
}

// "Structured Notes" — Stewardship Financial's note-monitoring board
// (2026-08-29). A SCOPE gate like canSeeIvansReview: these are Financial's
// instruments, watched by Jake and Brian Ream, plus the unscoped roles
// (Grant, Baker). Unlike the two gates around it this one DOES match the
// server's own rule — /api/structured-notes refuses a caller without
// Financial scope — so hiding the rail entry is not merely tidiness, it
// is keeping the nav honest about what the account can open.
export function canSeeStructuredNotes(user: RoleAwareUser): boolean {
  if (!user?.employee_role_slug) return false;
  if (user.employee_role_slug === "ambassador") return false;
  const scope = user.editable_company_slugs;
  if (scope === "all") return true;
  return Array.isArray(scope) && scope.includes("financial");
}

// "Fee Review" — Stewardship Financial's monthly advisory fee accuracy
// review (2026-09-01, the folded-in tools.stewardship.pro tool).
//
// Grant's call, 2026-09-01: "just me, Baker, and Jake." This is the
// OS's one PERSON-scoped gate: Grant, Baker and Jake by sign-in email,
// and no role opens it (since 2026-10-31, when a QA system_admin account
// was found walking in). MATCHES the server's own rule on
// /api/admin/financial/fee-review/* because both read
// shared/fee-review/access.ts, so the nav never offers a door that
// doesn't open.
export function canSeeFeeReview(user: RoleAwareUser): boolean {
  if (!user?.employee_role_slug) return false;
  return isFeeReviewPerson(user.email);
}

// Private Money settings — Mike's rules, the lenders' standing terms, the
// fund book, credit scores and each loan's underwriting (Grant,
// 2026-09-23: "Mike, Cole, Grant and Baker"). Everyone with Mortgage
// access still SEES all of it; this only decides whether the edit
// buttons are drawn. Same list the server checks
// (shared/private-money/pm-editors.ts), so a hidden button is never a
// door that would have opened.
export function canEditPrivateMoney(user: RoleAwareUser): boolean {
  return isPmEditor({ email: user?.email ?? null, roleSlug: user?.employee_role_slug ?? null });
}

// "Book of Business" — the Planning line-of-business breakdown (S20,
// ryan-planning-views.md §11). A SCOPE gate of the same kind as
// canSeeStructuredNotes, and like that one it matches the server's own
// rule: /api/dashboard/planning-book refuses a caller without Planning
// scope, so hiding the rail entry keeps the nav honest rather than
// merely tidy.
export function canSeePlanningBook(user: RoleAwareUser): boolean {
  if (!user?.employee_role_slug) return false;
  if (user.employee_role_slug === "ambassador") return false;
  const scope = user.editable_company_slugs;
  if (scope === "all") return true;
  return Array.isArray(scope) && scope.includes("planning");
}

// The Planning deposits screen (commissions step C6, part 2). The same
// rule as the server (shared/db/planning-statement-access.ts): anyone whose
// scope includes Planning, plus system admins and compliance. Unlike the
// Book of Business, an unscoped front desk does not see it: it is money.
export function canSeePlanningDeposits(user: RoleAwareUser): boolean {
  if (!user?.employee_role_slug) return false;
  if (user.employee_role_slug === "ambassador") return false;
  const scope = user.editable_company_slugs;
  if (Array.isArray(scope)) return scope.includes("planning");
  return scope === "all" && (user.employee_role_slug === "system_admin" || user.employee_role_slug === "compliance");
}

// "Call Recordings" sidebar entry (Compliance Program PR 16). A SCOPE
// gate like canSeeIvansReview: Medicare call recordings are Planning's
// work (Ryan, Andrea, Jake), so the rail shows the shortcut to
// planning-scoped users and the unscoped roles ("all" — Grant, Baker,
// Donna). Not a reach limit: the page itself is open to any employee
// (⌘K finds it), per "default to your desk" — narrow the rail, never
// the reach.
export function canSeeCallRecordingUpload(user: RoleAwareUser): boolean {
  if (!user?.employee_role_slug) return false;
  if (user.employee_role_slug === "ambassador") return false;
  const scope = user.editable_company_slugs;
  if (scope === "all") return true;
  return Array.isArray(scope) && scope.includes("planning");
}

// "Approved lenders" sidebar entry (2026-09-22). A SCOPE gate in the
// exact shape of canSeeCallRecordingUpload: the wholesale-lender roster is
// Stewardship Mortgage's working list (Joey, Mike, Cole, Cambria), so the
// rail shows it to mortgage-scoped users and the unscoped roles ("all" —
// Grant, Baker, Donna). Not a reach limit: /vendors is open to every
// employee and ⌘K finds the roster by name, per "default to your desk" —
// narrow the rail, never the reach.
export function canSeeApprovedLenders(user: RoleAwareUser): boolean {
  if (!user?.employee_role_slug) return false;
  if (user.employee_role_slug === "ambassador") return false;
  const scope = user.editable_company_slugs;
  if (scope === "all") return true;
  return Array.isArray(scope) && scope.includes("mortgage");
}

// Who may CHANGE the approved-lender list (Grant, 2026-09-24): the same
// Mortgage-scoped people who have it in their left panel. Kept as its own
// name so the day the two diverge is a one-line edit, not a hunt. The
// server enforces the same line (routes/vendors.ts, canActOnCompany), so
// this only decides whether the controls are drawn.
export function canEditApprovedLenders(user: RoleAwareUser): boolean {
  return canSeeApprovedLenders(user);
}

// Realty scope gate for the Realty Document Library
// (/esign/industry-documents/realty, formerly /admin/realty-forms-library)
// — which of the AAR catalog's blanks are on file, and mapping each
// one's fields with no deal needed.
//
// Originally the sidebar shortcut fixing a 2026-09-01 nav bug (Nate
// tried the page and it "doesn't exist" — a nav bug, not a server one:
// the page lived under ADMIN, which canSeeAdminSection() shows only to
// system_admin/compliance, even though every route behind it
// (/api/admin/contract-forms/*, /api/esign/templates/*) is gated with
// mayActOnCompany(scope, "realty") — any Realty-scoped employee, not
// just admins). That sidebar shortcut is gone: Industry Documents
// (2026-09-02) is open to every employee and self-scopes to the
// viewer's own company, so no Realty-specific rail entry is needed
// anymore. This function survives as the gate for the "Realty Document
// Library" ⌘K deep link (nav-route-index.ts's "realty_only" visibility)
// — the exact same scope rule, matching the server's own rule rather
// than guessing at it, same pattern as canSeeIvansReview.
export function canSeeRealtyFormsLibrary(user: RoleAwareUser): boolean {
  if (!user?.employee_role_slug) return false;
  if (user.employee_role_slug === "ambassador") return false;
  const scope = user.editable_company_slugs;
  if (scope === "all") return true;
  return Array.isArray(scope) && scope.includes("realty");
}

// Mortgage Compliance (/mortgage/compliance) — Baker's window onto the
// four documents an examiner asks for. Mortgage-scoped users plus the
// unscoped roles (Grant, Baker, Donna).
//
// A RAIL DEFAULT, not a reach limit — and the difference matters here,
// because the two sibling gates below it are the other kind. The three
// report routes behind this window are `requireAuth` with no company
// scope test, exactly as the Jake Test asks, so anyone CAN open them;
// this gate only decides whose left rail carries the shortcut. That is
// "default to your desk" doing its job: Joy does not start her day
// looking at mortgage audit documents, and she still reaches them by
// typing "loan log" in ⌘K, which CLAUDE.md carves out of scoping by name
// precisely so a narrowed rail never becomes a locked door.
//
// Fails closed on a user with no role information, same as its siblings.
export function canSeeMortgageCompliance(user: RoleAwareUser): boolean {
  if (!user?.employee_role_slug) return false;
  if (user.employee_role_slug === "ambassador") return false;
  const scope = user.editable_company_slugs;
  if (scope === "all") return true;
  return Array.isArray(scope) && scope.includes("mortgage");
}

// The Mortgage watch (/mortgage/watch; Marketing Hub row 10.5): credit
// inquiries on our own borrowers. Unlike Mortgage Compliance above, this is
// a REACH limit, because the server's is: gateInquiryDataAccess lets in
// Mortgage's own people, the system administrators and firm-wide
// compliance, and refuses everyone else, the unscoped front desk included.
// "all" alone is therefore not enough here (Donna is unscoped and refused);
// the role decides for the two firm-wide roles. UX only: the route is the
// real boundary, and this keeps a page that would 403 off the menu.
export function canSeeMortgageWatch(user: RoleAwareUser): boolean {
  if (!user?.employee_role_slug) return false;
  if (user.employee_role_slug === "ambassador") return false;
  const scope = user.editable_company_slugs;
  if (Array.isArray(scope) && scope.includes("mortgage")) return true;
  if (user.employee_role_slug === "system_admin") return true;
  return user.employee_role_slug === "compliance" && scope === "all";
}

// EMAIL section visibility. Office 365 phase 4+. Mylah is the
// documented identity override for back_office — per CLAUDE.md
// Team Roster she has no comms surfaces in the UI. Every other
// employee role (system_admin, company_admin, advisor, front_desk,
// compliance) sees the EMAIL section. Server-side, all /api/email/*
// routes still require an active Graph connection — so even
// front_desk who lacks a connection just sees an empty surface, not
// other advisors' mail.
export function canSeeEmailSection(user: RoleAwareUser): boolean {
  const slug = user?.employee_role_slug ?? null;
  if (slug === null) return false;
  if (slug === "back_office") return false;
  // Ambassador Agents get no email surfaces (Grant, 2026-08-07: no
  // mailbox in the OS for the referral-partner role).
  if (slug === "ambassador") return false;
  return true;
}

// Checklist review authority (accept/reject a submitted task) — Realty's
// broker sign-off today, any future company's reviewer tomorrow. This is
// UX only: it decides whether the Accept/Reject buttons render, not
// whether the request succeeds. The server (isChecklistReviewer in
// workers/api/src/routes/compliance.ts) is the real boundary and reads
// the same two facts — role + company scope — so the two cannot drift
// into different answers, only into a button that shows and then gets a
// 403, which is the acceptable failure mode per the retention schedule
// page's own precedent: "the endpoint remains the real boundary; the
// page just avoids a dead end."
/**
 * NOTE (2026-09-03): this is now only a PRE-FETCH GUESS, not the answer.
 *
 * Reviewer authority stopped being a pure function of role when checklist
 * authority grants landed: Mark Shipley may decide Realty's files through
 * an explicit grant row, and Nate Swartz — same role, same company — may
 * not. A grant is not on the session, so this predicate cannot see it and
 * will answer `false` for Mark.
 *
 * Surfaces that fetch checklist data should prefer the server's own
 * `can_review` flag (on GET /api/compliance/transactions/:id/checklist and
 * the review queue) and fall back to this only until that lands, which is
 * exactly what RealtyFileView does. The endpoints check again regardless,
 * so the worst case here is a button that 403s — never access it grants.
 */
export function canReviewCompanyChecklists(user: RoleAwareUser, company: string): boolean {
  const slug = user?.employee_role_slug ?? null;
  if (slug === "system_admin" || slug === "compliance") return true;
  if (slug !== "company_admin") return false;
  const scope = user?.editable_company_slugs;
  if (scope === "all" || scope == null) return true;
  return Array.isArray(scope) && scope.includes(company);
}

// "Send payment link" on the household Actions menu (Brandon, 2026-08-19).
//
// SCOPE, NOT ROLE — same shape as canSeeIvansReview above, and for the
// same reason: the seeded Payment Information form is owned by
// company_slug 'insurance', and the server gates minting its links on
// canSeeForm() → mayActOnCompany(actor, "insurance"). Joy (advisor),
// Brandon (company_admin) and Mylah (back_office) all service that book
// under three different roles, so gating on role would be wrong.
//
// "all" (Grant, Baker, Donna) passes. A user with no role information
// fails closed. This is UX only — it stops a mortgage-only user being
// handed a button that 403s; the endpoint remains the real boundary.
//
// If the payment form is ever widened past Insurance (Planning premiums,
// Mortgage servicing), this predicate and the form's company_slug move
// together — they are two halves of one decision.
export function canSendPaymentLink(user: RoleAwareUser): boolean {
  if (!user?.employee_role_slug) return false;
  if (user.employee_role_slug === "ambassador") return false;
  const scope = user.editable_company_slugs;
  if (scope === "all") return true;
  return Array.isArray(scope) && scope.includes("insurance");
}

// "Send long-term care prequal" on a household (LTC health intake, PR 4).
// The prequal is a Planning form, so the item shows for anyone whose
// company access includes Planning, and for unscoped roles. Brian Baker
// is a system admin, so he sees it. Hidden, not disabled, for everyone
// else. The server's company-scope check when it mints the link is the
// real boundary; this only keeps a button that would 403 off the menu.
export function canSendLtcPrequal(user: RoleAwareUser): boolean {
  if (!user?.employee_role_slug) return false;
  if (user.employee_role_slug === "ambassador") return false;
  const scope = user.editable_company_slugs;
  if (scope === "all") return true;
  return Array.isArray(scope) && scope.includes("planning");
}

// "Start an application for a borrower" (Mike, 2026-10-01). The two Private
// Money applications are Mortgage forms, so the button shows for anyone whose
// company access includes Mortgage, and for unscoped roles. Hidden, not
// disabled, for everyone else. The server's company-scope check when it mints
// the link (POST /api/forms/:slug/links) is the real boundary; this only keeps
// a button that would 403 off the screen.
export function canStartPmApplication(user: RoleAwareUser): boolean {
  if (!user?.employee_role_slug) return false;
  if (user.employee_role_slug === "ambassador") return false;
  const scope = user.editable_company_slugs;
  if (scope === "all") return true;
  return Array.isArray(scope) && scope.includes("mortgage");
}

// "Fill in a form with the client" on a household (Baker + Grant,
// 2026-10-14: every form). Every employee who talks to clients sees it;
// the window lists only the forms the server says they can see, and the
// server re-checks company scope (and the health gate on health forms)
// when it opens one. Hidden for the ambassador role (deny by default) and
// for back_office, which by roster talks only to carriers and vendors
// (Mylah: no intake or client communication).
export function canFillFormWithClient(user: RoleAwareUser): boolean {
  const slug = user?.employee_role_slug;
  if (!slug) return false;
  return slug !== "ambassador" && slug !== "back_office";
}
